import type { SV_COPY } from './en';

/**
 * हिन्दी — the svapp deck. Register: plain, warm, respectful आप-form (the droom's DEC-053 proving
 * run, carried forward). Established Indian-English terms (OTP, SMS, selfie, Chrome, Safari,
 * ID, video, camera) stay in Latin script, as Indian users read them. `**bold**` markers and
 * `{slots}` are preserved exactly. "Powered by KYC Hub" keeps the brand in Latin.
 *
 * REVIEW STATUS: authored 2026-09-13; the OTP front-door, identity-document and document-gate strings
 * (`sv.otp.*`, `sv.capture.doc.*`, `sv.gate.still.*.doc`) were added 2026-09-21; the round-2 phase-3 strings
 * (`sv.showme.*`, `sv.audio.*`) were added 2026-09-25, and the link-purpose words (`sv.door.purpose.*`, FSD-18 D7)
 * 2026-10-16; all of these are AWAITING NATIVE REVIEW — authored in this deck's
 * register, not a machine gloss, and not presented as final. Consent-adjacent strings need the standing VCIP-REG
 * glance and the whole deck rides the human-SME linguistic review before production cutover — that gate is
 * separate and undischarged. Preview only.
 */
export const HI_DECK: Record<keyof typeof SV_COPY, string> = {
  'sv.subtitle': 'स्थल सत्यापन',
  'sv.action.continue': 'आगे बढ़ें',
  'sv.action.tryAgain': 'फिर कोशिश करें',
  'sv.action.retake': 'दोबारा लें',
  'sv.action.back': 'पीछे',
  'sv.action.useThis': 'यह इस्तेमाल करें',
  'sv.action.useAnyway': 'फिर भी इस्तेमाल करें',
  'sv.action.allowed': 'मैंने अनुमति दे दी है',
  'sv.action.copyLink': 'सत्यापन लिंक कॉपी करें',
  'sv.action.linkCopied': 'लिंक कॉपी हो गया ✓',
  'sv.action.startRecording': 'रिकॉर्डिंग शुरू करें',
  'sv.action.stopRecording': 'रिकॉर्डिंग रोकें',
  'sv.action.takePhoto': 'फ़ोटो लें',
  'sv.action.flipCamera': 'कैमरा बदलें',
  'sv.action.turnOnCamera': 'कैमरा चालू करें',
  'sv.action.submit': 'जमा करें',
  'sv.action.reRecord': 'इसे दोबारा रिकॉर्ड करें',
  'sv.action.skipShot': 'यह शॉट छोड़ें',
  'sv.pill.secure': 'सुरक्षित',
  'sv.pill.submitted': 'जमा हो गया',
  'sv.poweredBy': 'Powered by KYC Hub',
  'sv.error.tryAgain': 'कृपया फिर कोशिश करें।',

  'sv.entry.preparing': 'आपका सत्यापन अनुरोध खोला जा रहा है…',
  'sv.entry.linkRequired.title': 'अपना सत्यापन लिंक खोलें',
  'sv.entry.linkRequired.body':
    'शुरू करने या आगे बढ़ने के लिए, SMS या email से मिला सुरक्षित लिंक खोलें।',
  'sv.entry.notFound.title': 'यह लिंक मान्य नहीं है',
  'sv.entry.notFound.body': 'कृपया अपने SMS या email से मिला सबसे नया लिंक इस्तेमाल करें।',
  'sv.entry.expired.title': 'इस लिंक की अवधि समाप्त हो गई है',
  'sv.entry.expired.body':
    'लिंक सीमित समय के लिए मान्य होते हैं। जिस संस्था ने इसे भेजा था, उससे नया लिंक भेजने को कहें।',
  'sv.entry.closed.title': 'यह अनुरोध अब खुला नहीं है',
  'sv.entry.closed.body':
    'हो सकता है यह पहले ही जमा हो चुका हो या वापस ले लिया गया हो। अगर आपको कुछ रिकॉर्ड करना था, तो लिंक भेजने वाली संस्था से संपर्क करें।',
  'sv.entry.unreachable.title': 'हम आपका अनुरोध नहीं खोल सके',
  'sv.entry.unreachable.body': 'अपना इंटरनेट जाँचकर फिर कोशिश करें। अभी तक कुछ भी रिकॉर्ड नहीं हुआ है।',

  'sv.door.title': 'इस अनुरोध की पुष्टि करें',
  'sv.door.sub': 'कुछ भी रिकॉर्ड होने से पहले, जाँच लें कि यह अनुरोध वही है जिसकी आपको उम्मीद थी।',
  'sv.door.client': 'अनुरोध करने वाला',
  'sv.door.purpose': 'उद्देश्य',
  'sv.door.purpose.CAPTURE': 'आपके परिसर की video जाँच',
  'sv.door.purpose.RECAPTURE': 'कुछ शॉट दोबारा रिकॉर्ड करना',
  'sv.door.purpose.REVISIT': 'आपके परिसर का दोबारा दौरा',
  'sv.door.purpose.REVERIFY': 'आपके परिसर की नई जाँच',
  'sv.door.purpose.FIELD_VISIT': 'फ़ील्ड एजेंट का दौरा',
  'sv.door.purpose.other': 'आपके परिसर की जाँच',
  'sv.door.reference': 'संदर्भ',
  'sv.door.premises': 'परिसर',
  'sv.door.kind.OFFICE': 'दफ़्तर',
  'sv.door.kind.SHOP': 'दुकान',
  'sv.door.kind.FACTORY': 'फ़ैक्टरी',
  'sv.door.kind.WAREHOUSE': 'गोदाम',
  'sv.door.kind.RESIDENCE': 'घर',
  'sv.door.kind.OTHER': 'परिसर',
  'sv.door.captureList': 'आपसे क्या-क्या रिकॉर्ड करने को कहा जाएगा',
  'sv.door.time': 'इसमें लगभग 12 से 20 मिनट लगते हैं, इसी फ़ोन पर, परिसर में रहते हुए।',
  'sv.door.never': '{tenantName} आपसे इस पेज के बाहर कभी भी OTP, password या दस्तावेज़ नहीं माँगेगा।',
  'sv.door.neverGeneric':
    'क्लाइंट आपसे इस पेज के बाहर कभी भी OTP, password या दस्तावेज़ नहीं माँगेगा।',
  'sv.door.concern.trigger': 'कुछ गड़बड़ लग रहा है? चिंता दर्ज करें',
  'sv.door.concern.title': 'अगर यह अनुरोध गलत लगे',
  'sv.door.concern.body':
    'आगे न बढ़ें। {tenantName} से उस माध्यम से संपर्क करें जिस पर आप पहले से भरोसा करते हैं — वह नंबर या पता जो इस संदेश से पहले आपके पास था, इस पेज का नहीं।',
  'sv.door.concern.body2':
    'जब तक आप आगे नहीं बढ़ते, कुछ भी रिकॉर्ड नहीं होता, और क्लाइंट की ओर से कोई भी इन चरणों के बाहर आपसे कोड, password या दस्तावेज़ की फ़ोटो नहीं माँगेगा।',
  'sv.door.concern.stop': 'रुकें और यह पेज बंद करें',
  'sv.door.concern.stopped.title': 'कुछ भी रिकॉर्ड नहीं हुआ',
  'sv.door.concern.stopped.body':
    'आप यह पेज बंद कर सकते हैं। अगर बाद में आगे बढ़ना चाहें, तो यही लिंक फिर से खोलें।',
  'sv.door.cta': 'यही अपेक्षित था — आगे बढ़ें',

  'sv.otp.title': 'हमने जो code भेजा है, वह दर्ज करें',
  'sv.otp.sent.EMAIL': 'हमने {target} पर 6 अंकों का code भेजा है। अपना inbox देखें, spam folder भी।',
  'sv.otp.sent.SMS': 'हमने {target} पर SMS से 6 अंकों का code भेजा है।',
  'sv.otp.validFor': 'यह code 10 मिनट तक और केवल इसी पेज पर काम करता है।',
  'sv.otp.label': '6 अंकों का code',
  'sv.otp.verify': 'Code की पुष्टि करें',
  'sv.otp.verifying': 'Code जाँचा जा रहा है…',
  'sv.otp.never':
    'हम आपसे यह code फ़ोन, message या आमने-सामने कभी नहीं माँगेंगे। इसे केवल यहीं दर्ज करें।',
  'sv.otp.resend': 'Code नहीं मिला? जिस संस्था ने आपको लिंक भेजा था, उससे दोबारा भेजने को कहें।',
  'sv.otp.wrong': 'यह code सही नहीं है। जाँचकर फिर कोशिश करें।',
  'sv.otp.wrong.attempts': 'यह code सही नहीं है। इस लिंक के लॉक होने से पहले आपके पास {n} और मौके हैं।',
  'sv.otp.wrong.last': 'यह code सही नहीं है। आपके पास 1 और मौका है — उसके बाद यह लिंक लॉक हो जाएगा।',
  'sv.otp.locked.title': 'यह लिंक लॉक हो गया है',
  'sv.otp.locked.body':
    'बहुत बार गलत code दर्ज किया गया, इसलिए यह लिंक अब काम नहीं करेगा। संस्था को आपको नया लिंक भेजना होगा। अभी तक कुछ भी रिकॉर्ड नहीं हुआ है।',
  'sv.otp.expired.title': 'उस code की अवधि समाप्त हो गई है',
  'sv.otp.expired.body': 'Code 10 मिनट तक काम करता है। जिस संस्था ने आपको लिंक भेजा था, उससे दोबारा भेजने को कहें।',
  'sv.otp.unavailable.title': 'हम आपको code नहीं भेज सके',
  'sv.otp.unavailable.body':
    'यह संस्था जिस तरीके से code भेजती है, वह अभी उपलब्ध नहीं है, इसलिए यह लिंक अभी नहीं खुल सकता। अभी तक कुछ भी रिकॉर्ड नहीं हुआ है। संस्था से उस माध्यम से संपर्क करें जिस पर आप पहले से भरोसा करते हैं।',
  'sv.otp.unreachable': 'हम code नहीं जाँच सके। अपना इंटरनेट जाँचकर फिर कोशिश करें।',
  'sv.otp.failed': 'हम अभी code नहीं जाँच सके। कृपया फिर कोशिश करें।',

  'sv.consent.title': 'आपकी सहमति',
  'sv.consent.sub':
    'चुनें कि {tenantName} क्या रिकॉर्ड और इस्तेमाल कर सकता है। "आवश्यक" चिह्नित चीज़ें इस सत्यापन के लिए ज़रूरी हैं।',
  'sv.consent.notice': 'सूचना संस्करण {version}',
  'sv.consent.required': 'आवश्यक',
  'sv.consent.optional': 'वैकल्पिक',
  'sv.consent.purpose.PREMISES_VIDEO.t': 'परिसर के video और फ़ोटो',
  'sv.consent.purpose.PREMISES_VIDEO.d':
    'परिसर के बाहर और अंदर के छोटे clip और फ़ोटो, जो इस सत्यापन के रिकॉर्ड के रूप में रखे जाते हैं।',
  'sv.consent.purpose.PERSONS_IN_FRAME.t': 'रिकॉर्डिंग में दिखने वाले लोग',
  'sv.consent.purpose.PERSONS_IN_FRAME.d':
    'selfie के लिए आपका चेहरा, और जो भी संयोग से फ़्रेम में आ जाए। साझा की गई प्रतियों पर आपके अलावा बाकी चेहरे धुँधले कर दिए जाते हैं।',
  'sv.consent.purpose.GEOLOCATION.t': 'रिकॉर्डिंग के समय आपकी लोकेशन',
  'sv.consent.purpose.GEOLOCATION.d':
    'हर शॉट पर फ़ोन की लोकेशन, जिसकी तुलना पंजीकृत पते से की जाती है।',
  'sv.consent.purpose.IDENTITY_DOCUMENT.t': 'आपका पहचान दस्तावेज़',
  'sv.consent.purpose.IDENTITY_DOCUMENT.d':
    'आपके ID की एक फ़ोटो, जो सिर्फ़ यह पुष्टि करने के लिए है कि इस परिसर के प्रतिनिधि आप ही हैं।',
  'sv.consent.purpose.LOCATION.t': 'रिकॉर्डिंग के समय आपकी लोकेशन',
  'sv.consent.purpose.LOCATION.d':
    'हर शॉट पर फ़ोन की लोकेशन — और वह कैसे मिली — जिसकी तुलना पंजीकृत पते से की जाती है।',
  'sv.consent.purpose.REGISTRY_CROSS_MATCH.t': 'सार्वजनिक रजिस्ट्री रिकॉर्ड की जाँच',
  'sv.consent.purpose.REGISTRY_CROSS_MATCH.d':
    '{tenantName} को पहले से दिए गए व्यवसाय के पहचान-क्रमांक (जैसे GSTIN या उद्यम) केवल संबंधित सार्वजनिक रिकॉर्ड देखने के लिए इस्तेमाल होते हैं।',
  'sv.consent.purpose.DATA_RETENTION.t': 'सील किया हुआ रिकॉर्ड रखना',
  'sv.consent.purpose.DATA_RETENTION.d':
    'रिकॉर्डिंग और ऊपर बताया गया डेटा, सूचना में बताई गई अवधि तक सील और अपरिवर्तित रखा जाता है, फिर हटा दिया जाता है।',
  'sv.consent.noticeFull': 'पूरी सूचना',
  'sv.consent.noticeControlling': '(अंग्रेज़ी पाठ संदर्भ संस्करण है)',
  'sv.consent.noticeControlling.ar': '(अरबी पाठ संदर्भ संस्करण है)',
  'sv.consent.purpose.ONWARD_SHARING.t': 'नतीजा आगे साझा करना',
  'sv.consent.purpose.ONWARD_SHARING.d':
    'सत्यापन रिपोर्ट और रिकॉर्डिंग उस विनियमित संस्था के साथ साझा की जा सकती हैं, जिसकी ओर से {tenantName} सत्यापन कर रहा है।',
  'sv.consent.residence':
    'यह एक घर है। रिकॉर्डिंग में दिखेगा कि आप कहाँ रहते हैं। साझा की गई प्रतियों पर सड़क-स्तर की सटीक लोकेशन नहीं दिखाई जाती, और रिकॉर्डिंग सिर्फ़ इस पते की पुष्टि के लिए इस्तेमाल होती है।',
  'sv.consent.dwelling':
    'यह ऐसा आवास है जहाँ लोग रहते हैं। केवल प्रवेश द्वार और साझा जगहें फ़िल्म करें — कभी भी शयनकक्ष, सोने की जगहें या यहाँ रहने वाले लोग नहीं। साझा की गई प्रतियों पर केवल क्षेत्र दिखता है, सटीक स्थान कभी नहीं।',
  'sv.consent.bystander.t': 'दूसरे लोगों को फ़्रेम से बाहर रखें',
  'sv.consent.bystander.d':
    'अंदर रिकॉर्ड करने से पहले, जिन लोगों का रिकॉर्डिंग में होना ज़रूरी नहीं है, उनसे एक तरफ़ हटने को कहें। लोगों को तभी रिकॉर्ड करें जब टाला न जा सके।',
  'sv.consent.retention':
    'रिकॉर्डिंग उतने समय तक रखी जाती हैं जितना संस्था ने तय किया है। सत्यापन का उद्देश्य पूरा होने पर आप उनसे सुधार या मिटाने के बारे में पूछ सकते हैं।',
  'sv.consent.affirm':
    'मैं इस सत्यापन के लिए {tenantName} को ऊपर मेरी अनुमति दी गई चीज़ें रिकॉर्ड और इस्तेमाल करने की **सहमति** देता/देती हूँ।',
  'sv.consent.agree': 'सहमत हूँ, आगे बढ़ें',
  'sv.consent.recording': 'आपकी सहमति दर्ज की जा रही है…',
  'sv.consent.recorded': 'सहमति दर्ज हो गई',
  'sv.consent.declined.title': 'यह सत्यापन आगे नहीं बढ़ सकता',
  'sv.consent.declined.body':
    'एक आवश्यक चीज़ की अनुमति नहीं दी गई: {purpose}। इसके बिना यह सत्यापन इस तरह पूरा नहीं हो सकता। आप अपना चुनाव बदल सकते हैं, या यह पेज बंद करके किसी और विकल्प के लिए संस्था से संपर्क कर सकते हैं।',
  'sv.consent.declined.review': 'मेरे चुनाव फिर देखें',
  'sv.consent.postfail': 'हम आपकी सहमति दर्ज नहीं कर सके। अपना इंटरनेट जाँचकर फिर कोशिश करें।',
  'sv.consent.live.group': 'अधिकारी के साथ लाइव वीडियो कॉल के लिए',
  'sv.consent.purpose.LIVE_VIDEO.t': 'आपका लाइव video रिकॉर्ड किया जाता है',
  'sv.consent.purpose.LIVE_VIDEO.d':
    '{tenantName} का एक अधिकारी रिकॉर्ड की जाने वाली video कॉल पर आपको देखता है और आपसे बात करता है; आपका चेहरा और आवाज़ रिकॉर्डिंग का हिस्सा होते हैं।',
  'sv.consent.purpose.LIVE_IDENTITY_CHECK.t': 'जीवंतता जाँच और चेहरे का मिलान',
  'sv.consent.purpose.LIVE_IDENTITY_CHECK.d':
    'सॉफ़्टवेयर जाँचता है कि video में कोई जीवित व्यक्ति है, और आपके चेहरे की तुलना आपकी पहले ली गई selfie से करता है। नतीजा एक व्यक्ति तय करता है।',
  'sv.consent.purpose.LIVE_CUSTODY_RETENTION.t': 'कॉल की रिकॉर्डिंग {tenantName} के video-कॉल रिकॉर्ड रखने के नियमों के तहत रखी जाती है',
  'sv.consent.purpose.LIVE_CUSTODY_RETENTION.d': 'ऊपर दिए गए स्थल-सत्यापन के नियमों के तहत नहीं। अवधि पूरी सूचना में बताई गई है।',

  'sv.capture.preparing': 'आपके रिकॉर्डिंग चरण तैयार हो रहे हैं…',
  'sv.capture.startFail.title': 'हम आपके रिकॉर्डिंग चरण लोड नहीं कर सके',
  'sv.capture.startFail.body': 'कृपया अपना इंटरनेट जाँचकर फिर कोशिश करें। कुछ भी खोया नहीं है।',
  'sv.capture.progress': 'चरण {n} / {total}',
  'sv.capture.mandatory': 'आवश्यक',
  'sv.capture.optional': 'वैकल्पिक — आप इसे छोड़ सकते हैं',
  'sv.capture.clip': 'छोटा clip',
  'sv.capture.still': 'फ़ोटो',
  'sv.capture.orientation.LANDSCAPE': 'फ़ोन को आड़ा (landscape) पकड़ें',
  'sv.capture.orientation.PORTRAIT': 'फ़ोन को सीधा (portrait) पकड़ें',
  'sv.capture.orientation.ANY': 'किसी भी दिशा में',
  'sv.capture.orientation.rotate': 'मिलाने के लिए फ़ोन घुमाएँ',
  'sv.capture.duration': '{min} से {max} सेकंड के बीच रिकॉर्ड करें',
  'sv.capture.durationMin': 'कम से कम {min} सेकंड',
  'sv.capture.elapsed': '{s} से.',
  'sv.capture.recording': 'रिकॉर्डिंग जारी है',
  'sv.capture.challenge.SHOW_NONCE': 'कोड फ़्रेम में दिखाएँ',
  'sv.capture.challenge.SPEAK_NONCE': 'कोड ज़ोर से बोलें',
  'sv.capture.challenge.ACTION': 'रिकॉर्ड करते हुए यह करें',
  'sv.capture.challenge.why': 'इससे पता चलता है कि clip अभी, इसी अनुरोध के लिए रिकॉर्ड हुआ है।',
  'sv.capture.visitCode': 'विज़िट कोड {code}',
  'sv.capture.micOff':
    'आपका microphone बंद है, इसलिए बोला गया कोड सुनाई नहीं देगा। microphone की अनुमति दें, वरना समीक्षक आपसे इसे दोबारा रिकॉर्ड करने को कह सकते हैं।',
  'sv.capture.camera.badge.rear': 'पीछे का कैमरा',
  'sv.capture.camera.badge.front': 'सामने का कैमरा',
  'sv.capture.camera.badge.camera': 'कैमरा',
  'sv.capture.camera.badge.preview': 'पूर्वावलोकन',
  'sv.capture.camera.primer':
    'बटन दबाने पर कैमरा चालू होता है। जब तक आप शुरू नहीं करते, कुछ भी रिकॉर्ड नहीं होता।',
  'sv.capture.camera.starting': 'आपका कैमरा शुरू हो रहा है…',
  'sv.capture.camera.checking': 'कैमरे की अनुमति जाँची जा रही है…',
  'sv.capture.camera.notready': 'कैमरा अभी शुरू हो रहा है — एक पल रुककर फिर कोशिश करें।',
  'sv.capture.motion.primer':
    'आपका फ़ोन गति (motion) डेटा साझा करने की अनुमति माँग सकता है। इससे पता चलता है कि clip यहीं, हाथ से रिकॉर्ड हुआ है।',
  'sv.capture.hint.lineup': 'शॉट सेट करें और फ़ोन स्थिर रखें।',
  // ── capture chrome (SPEC_CAPTURE_UI_20260925): the bar, the grid + level, the location card ──
  'sv.action.flipShort': 'बदलें',
  'sv.action.skipShort': 'छोड़ें',
  'sv.capture.grid.label': 'ग्रिड',
  'sv.capture.grid.hide': 'ग्रिड छिपाएँ',
  'sv.capture.grid.show': 'ग्रिड दिखाएँ',
  'sv.capture.level.level': 'सीधा',
  'sv.capture.level.left': '{deg}° बाएँ',
  'sv.capture.level.right': '{deg}° दाएँ',
  'sv.capture.location.aria': 'वह लोकेशन जो यह शॉट रिकॉर्ड करेगा',
  'sv.capture.location.waiting': 'लोकेशन फ़िक्स का इंतज़ार है…',
  'sv.capture.location.denied': 'लोकेशन की अनुमति बंद है — शॉट इसके बिना रिकॉर्ड होगा',
  'sv.capture.location.unsupported': 'इस डिवाइस पर लोकेशन उपलब्ध नहीं है — शॉट इसके बिना रिकॉर्ड होगा',
  'sv.capture.location.held': 'इस शॉट के लिए लोकेशन फ़िक्स रखा गया है',
  'sv.capture.location.bound': 'इस शॉट से बंधा',
  'sv.capture.location.accuracy': '± {m} मीटर, डिवाइस के अनुसार',
  'sv.capture.location.distance': 'पंजीकृत पते से दूरी: कैप्चर के बाद सर्वर पर मापी जाएगी',
  'sv.capture.location.justNow': 'अभी-अभी',
  'sv.capture.location.agoS': '{s} सेकंड पहले',
  'sv.capture.location.agoMin': '{m} मिनट पहले',
  'sv.capture.location.footer': 'यह शॉट क्या रिकॉर्ड करेगा',
  'sv.action.details': 'विवरण',
  'sv.action.detailsHide': 'विवरण छिपाएँ',
  // ── the stamp preview lines (SPEC_STAMP_ROUND §C.3, M3) ──
  'sv.capture.stamp.plusCode': 'Plus code {code}',
  'sv.capture.stamp.distance.about': 'पंजीकृत पते से लगभग {d}',
  'sv.capture.stamp.band.B50': '50 मीटर के भीतर',
  'sv.capture.stamp.band.B200': '200 मीटर के भीतर',
  'sv.capture.stamp.band.B1000': '1 किमी के भीतर',
  'sv.capture.stamp.band.OVER': '1 किमी से ज़्यादा',
  'sv.capture.stamp.distance.centroid': 'पंजीकृत पते से दूरी: तय नहीं हो सकी (केवल पिनकोड का केंद्र उपलब्ध है)',
  'sv.capture.stamp.copyNote': 'निर्देशांक साझा की गई प्रतियों पर नहीं छपते',
  'sv.capture.stamp.address': 'पंजीकृत पता ({tenantName} के रिकॉर्ड के अनुसार)',
  'sv.capture.stamp.watch.denied': 'लोकेशन की अनुमति बंद है — इस फ़ोन का आख़िरी फ़िक्स दिखाया जा रहा है',
  'sv.capture.stamp.watch.unavailable': 'लोकेशन अपडेट उपलब्ध नहीं — आख़िरी फ़िक्स दिखाया जा रहा है',
  'sv.capture.stamp.watch.timeout': 'अभी कोई नया फ़िक्स नहीं — आख़िरी फ़िक्स दिखाया जा रहा है',
  'sv.capture.stamp.watch.unsupported': 'इस डिवाइस पर लोकेशन अपडेट उपलब्ध नहीं हैं — आख़िरी फ़िक्स दिखाया जा रहा है',
  'sv.capture.stamp.map.unavailable': 'मैप अभी उपलब्ध नहीं है',
  'sv.capture.stamp.map.title': 'मैप',
  'sv.capture.stamp.map.open': 'मैप खोलें',
  'sv.capture.stamp.map.close': 'मैप बंद करें',
  'sv.capture.stamp.map.here': 'आप यहाँ हैं',
  'sv.capture.stamp.map.fixed': 'आपकी स्थिति के आसपास का स्थिर दृश्य — इस संस्करण में पैन या ज़ूम नहीं है',
  'sv.capture.stamp.map.anchorOff': 'पंजीकृत पता इस मैप से बाहर है, {side}',
  'sv.capture.stamp.map.side.N': 'उत्तर की ओर',
  'sv.capture.stamp.map.side.NE': 'उत्तर-पूर्व की ओर',
  'sv.capture.stamp.map.side.E': 'पूर्व की ओर',
  'sv.capture.stamp.map.side.SE': 'दक्षिण-पूर्व की ओर',
  'sv.capture.stamp.map.side.S': 'दक्षिण की ओर',
  'sv.capture.stamp.map.side.SW': 'दक्षिण-पश्चिम की ओर',
  'sv.capture.stamp.map.side.W': 'पश्चिम की ओर',
  'sv.capture.stamp.map.side.NW': 'उत्तर-पश्चिम की ओर',
  'sv.capture.hint.selfie': 'समान रोशनी में अपना पूरा चेहरा अंडाकार घेरे के अंदर रखें।',
  'sv.capture.hint.doc': 'पूरा दस्तावेज़ बॉक्स के अंदर, सपाट और बिना चमक के रखें।',
  'sv.capture.doc.guide.title': 'दस्तावेज़ की फ़ोटो लेने से पहले',
  'sv.capture.doc.guide.frame':
    'कार्ड को सपाट रखें और पूरा कार्ड बॉक्स के अंदर रखें, चारों कोने दिखते हुए।',
  'sv.capture.doc.guide.glare':
    'चमक और छाया से बचें: खिड़की और लैंप से दूर मुड़ें, और flash बंद रखें।',
  'sv.capture.doc.guide.lens':
    'पीछे वाला कैमरा इस्तेमाल करें, फ़ोन स्थिर रखें, और फ़ोटो लेने से पहले उसे focus होने दें।',
  'sv.capture.doc.stored':
    'आपके दस्तावेज़ की फ़ोटो इस विज़िट के सबूत के रूप में उसी सुरक्षा के साथ रखी जाती है जैसे यहाँ ली गई हर फ़ोटो, और इसे केवल आपके मामले की समीक्षा करने वाले लोग देखते हैं। जहाँ भी नंबर लिखकर दिखाया जाता है, वहाँ केवल उसके आखिरी 4 अक्षर दिखते हैं।',
  'sv.capture.doc.storedMasked':
    'यह फ़ोटो रखे जाने से पहले, इस पर आधार नंबर के पहले 8 अंक, कोई भी VID और कोई भी QR कोड काले कर दिए जाते हैं। इसे केवल आपके मामले की समीक्षा करने वाले लोग देखते हैं, और नंबर जहाँ भी लिखा जाता है, वहाँ केवल उसके आखिरी 4 अक्षर दिखते हैं।',
  'sv.capture.doc.storedPolicy':
    'आपके पहचान-पत्र की फ़ोटो इस विज़िट के सबूत के रूप में उसी सुरक्षा के साथ रखी जाती है जैसे यहाँ ली गई हर फ़ोटो, और इसे केवल आपके मामले की समीक्षा करने वाले लोग देखते हैं।',
  'sv.capture.hint.dark': 'रोशनी कम लग रही है — ज़्यादा रोशनी वाली जगह जाएँ।',
  'sv.capture.hint.bright': 'रोशनी बहुत तेज़ लग रही है — सीधी रोशनी से हटें।',
  'sv.capture.hint.blurry': 'धुँधला लग रहा है — फ़ोन स्थिर रखें और कैमरे को focus होने दें।',
  'sv.capture.assessing': 'आपके डिवाइस पर गुणवत्ता जाँची जा रही है…',
  'sv.capture.preview.ok': 'ठीक लग रहा है — इसे इस्तेमाल करें, या दोबारा लें।',
  'sv.capture.preview.notEvaluated':
    'इस डिवाइस पर गुणवत्ता नहीं जाँची जा सकी। समीक्षक इसे जाँचेंगे।',
  'sv.capture.face.none': 'हमें चेहरा नहीं दिखा। अपना चेहरा अंडाकार घेरे में रखकर फ़ोटो दोबारा लें।',
  'sv.capture.face.notEvaluated': 'चेहरे की जाँच आपकी फ़ोटो की समीक्षा के समय होगी।',
  'sv.capture.queued': 'इस फ़ोन पर सहेज लिया गया — पीछे-पीछे अपलोड हो रहा है।',
  'sv.capture.uploading': '{n} हिस्से अभी अपलोड हो रहे हैं',
  'sv.capture.refused.title': 'यह शॉट जैसा भेजा गया वैसा स्वीकार नहीं हो सका',
  'sv.capture.refused.body':
    'सर्वर पुष्टि नहीं कर सका कि यह रिकॉर्डिंग वही है जो आपके फ़ोन ने भेजी थी, इसलिए इसे स्वीकार नहीं किया गया। कृपया इसे दोबारा रिकॉर्ड करें।',
  'sv.capture.refused.code': 'कारण कोड {code}',
  'sv.capture.refused.doc.title': 'आपके दस्तावेज़ की यह फ़ोटो इस्तेमाल नहीं हो सकी',
  'sv.capture.refused.doc.body':
    'फ़ोटो साफ़ नहीं है, या यह ऐसा दस्तावेज़ नहीं है जिसे हम स्वीकार कर सकें। दस्तावेज़ को सपाट रखकर, अच्छी रोशनी में फिर से फ़ोटो लें। हम आधार, PAN, पासपोर्ट, ड्राइविंग लाइसेंस या वोटर ID स्वीकार करते हैं।',
  'sv.capture.refused.doc.bodyPolicy':
    'फ़ोटो इतनी साफ़ नहीं है कि इस्तेमाल हो सके। पूरे दस्तावेज़ को सपाट रखकर, अच्छी रोशनी में फिर से फ़ोटो लें।',
  'sv.capture.refused.photo.title': 'यह फ़ोटो इस्तेमाल नहीं हो सकी',
  'sv.capture.refused.photo.body': 'यह फ़ोटो खोली नहीं जा सकी। कृपया नई फ़ोटो लें।',
  'sv.capture.sealed': 'स्वीकार हो गया',
  'sv.capture.footer':
    'आपके डिवाइस पर रिकॉर्ड, यहीं गुणवत्ता-जाँच, और अपलोड पर hash chain से सील।',
  'sv.capture.bystander.title': 'अंदर रिकॉर्ड करने से पहले',
  'sv.capture.bystander.body':
    'जिन लोगों का रिकॉर्डिंग में होना ज़रूरी नहीं है, उनसे एक तरफ़ हटने को कहें। अगर लोगों का दिखना ज़रूरी है, तो कोई बात नहीं — साझा की गई प्रतियों पर उनके चेहरे धुँधले कर दिए जाते हैं।',
  'sv.capture.bystander.cta': 'समझ गया/गई — आगे बढ़ें',
  'sv.capture.recaptureOnly':
    'सिर्फ़ नीचे दिए शॉट दोबारा माँगे गए हैं। आपने जो बाकी भेजा था, वह सुरक्षित है।',
  'sv.capture.signature.primer':
    'रिकॉर्डिंग पर हस्ताक्षर के लिए आपका फ़ोन fingerprint या चेहरा माँग सकता है। यह इसी डिवाइस की एक key से हस्ताक्षर करता है; आपका fingerprint साझा नहीं होता।',
  'sv.capture.signature.unavailable':
    'यह डिवाइस रिकॉर्डिंग पर हस्ताक्षर नहीं कर सकता। आपकी रिकॉर्डिंग फिर भी स्वीकार होती है; समीक्षक देखेंगे कि डिवाइस-हस्ताक्षर संभव नहीं था।',

  'sv.gate.clip.short': 'यह clip बहुत छोटा है। कम से कम दिखाई गई अवधि तक रिकॉर्ड करें।',
  'sv.gate.clip.empty': 'कुछ भी रिकॉर्ड नहीं हुआ — कृपया फिर से रिकॉर्ड करें।',
  'sv.gate.clip.cameraStopped': 'कैमरा रुक गया — यह चरण फिर से रिकॉर्ड करें।',
  'sv.gate.clip.dark': 'इस clip का ज़्यादातर हिस्सा बहुत अँधेरा था। ज़्यादा रोशनी में इसे दोबारा रिकॉर्ड करें।',
  'sv.gate.clip.bright': 'इस clip का ज़्यादातर हिस्सा बहुत चमकीला था। सीधी रोशनी से हटकर इसे दोबारा रिकॉर्ड करें।',
  'sv.gate.clip.blurry': 'यह clip focus में नहीं था। फ़ोन स्थिर रखें, कैमरे को focus होने दें, और दोबारा रिकॉर्ड करें।',
  'sv.gate.clip.notEvaluated': 'इस डिवाइस पर गुणवत्ता नहीं जाँची जा सकी।',
  'sv.gate.still.small': 'साफ़ प्रति के लिए यह फ़ोटो बहुत छोटी है। पास जाएँ या पीछे का कैमरा इस्तेमाल करें।',
  'sv.gate.still.small.doc':
    'दस्तावेज़ पढ़ने लायक होने के लिए यह फ़ोटो बहुत छोटी है। पास जाएँ या पीछे का कैमरा इस्तेमाल करें।',
  'sv.gate.still.dark': 'यह फ़ोटो बहुत अँधेरी है। ज़्यादा रोशनी में इसे दोबारा लें।',
  'sv.gate.still.dark.doc': 'दस्तावेज़ पढ़ने के लिए बहुत अँधेरा है। ज़्यादा रोशनी में जाकर फिर से लें।',
  'sv.gate.still.bright': 'यह फ़ोटो बहुत चमकीली है। सीधी रोशनी से हटकर इसे दोबारा लें।',
  'sv.gate.still.glare.doc':
    'दस्तावेज़ पर चमक है। सीधी रोशनी से दूर जाएँ, flash बंद रखें, और फिर से लें।',
  'sv.gate.still.blurry': 'यह फ़ोटो धुँधली है। फ़ोन स्थिर रखें, कैमरे को focus होने दें, और दोबारा लें।',
  'sv.gate.still.blurry.doc':
    'दस्तावेज़ धुँधला है। फ़ोन स्थिर रखें, कैमरे को focus होने दें, और फिर से लें।',
  'sv.gate.still.unreadable': 'हम वह फ़ोटो पढ़ नहीं सके। कृपया इसे दोबारा लें।',

  'sv.cam.denied.title': 'कैमरे की अनुमति बंद है',
  'sv.cam.denied.body':
    'आपके browser में इस पेज के लिए कैमरा बंद है। नीचे दिए चरण पूरे करें, फिर बटन दबाएँ।',
  'sv.cam.osdenied.title': 'आपका डिवाइस कैमरा रोक रहा है',
  'sv.cam.osdenied.body':
    'अनुमति का prompt आया ही नहीं, जिसका मतलब आमतौर पर यह है कि कोई सिस्टम सेटिंग browser के कैमरे को रोक रही है।',
  'sv.cam.nodevice.title': 'कोई कैमरा नहीं मिला',
  'sv.cam.nodevice.fallback': 'इस डिवाइस पर हमें कोई काम करने वाला कैमरा नहीं मिला।',
  'sv.cam.nodevice.hookDetail': 'इस डिवाइस पर कोई कैमरा नहीं मिला।',
  'sv.cam.stalled.title': 'आपका कैमरा तस्वीर नहीं भेज रहा है — इसे फिर से चालू करने के लिए टैप करें।',
  'sv.cam.stalled.restart': 'कैमरा फिर से चालू करें',
  'sv.cam.webview.title': 'यह लिंक अपने browser में खोलें',
  'sv.cam.webview.body':
    'इस app का अंदरूनी browser कैमरा इस्तेमाल नहीं कर सकता। लिंक कॉपी करके Chrome या Safari में खोलें।',
  'sv.cam.unsupported.title': 'यह browser video रिकॉर्ड नहीं कर सकता',
  'sv.cam.unsupported.body': 'कृपया लिंक को Chrome या Safari के नए संस्करण में खोलें।',
  'sv.perm.steps.aria': 'कैमरे की अनुमति देने के चरण',
  'sv.perm.webview.tip': 'सुझाव: menu (⋮ या share आइकन) दबाकर "Open in browser" चुनें।',
  'sv.perm.rec.os.1': 'अपने फ़ोन की Settings खोलें।',
  'sv.perm.rec.os.2': 'अपना browser (Chrome या Safari) ढूँढकर उसकी permissions खोलें।',
  'sv.perm.rec.os.3': 'Camera और Microphone चालू करें, फिर यहाँ वापस आएँ।',
  'sv.perm.rec.chrome.1': 'पते के बगल में lock आइकन दबाएँ।',
  'sv.perm.rec.chrome.2': 'Permissions दबाएँ, फिर Camera।',
  'sv.perm.rec.chrome.3': 'Allow चुनें, फिर नीचे का बटन दबाएँ।',
  'sv.perm.rec.firefox.1': 'पते के बगल में lock आइकन दबाएँ।',
  'sv.perm.rec.firefox.2': 'Site permissions दबाएँ।',
  'sv.perm.rec.firefox.3': 'Camera को Allow पर सेट करें, फिर नीचे का बटन दबाएँ।',
  'sv.perm.rec.safari.1': 'address bar में "AA" आइकन दबाएँ।',
  'sv.perm.rec.safari.2': 'Website Settings दबाएँ।',
  'sv.perm.rec.safari.3': 'Camera को Allow पर सेट करें, फिर नीचे का बटन दबाएँ।',
  'sv.perm.rec.generic.1': 'इस पेज के लिए अपने browser की site settings खोलें।',
  'sv.perm.rec.generic.2': 'permissions की सूची में Camera ढूँढें।',
  'sv.perm.rec.generic.3': 'इसे Allow पर सेट करें, फिर नीचे का बटन दबाएँ।',
  'sv.perm.osnotice.ios':
    'आपका डिवाइस हमारे न्यूनतम समर्थन ({min}) से नीचे है। आप कोशिश कर सकते हैं, पर अगर कैमरा काम न करे तो कृपया update करें या दूसरा फ़ोन इस्तेमाल करें।',
  'sv.perm.osnotice.android':
    'आपका browser हमारे न्यूनतम समर्थन ({min}) से नीचे है। आप कोशिश कर सकते हैं, पर अगर कैमरा काम न करे तो कृपया update करें या दूसरा फ़ोन इस्तेमाल करें।',
  'sv.perm.osnotice.samsung':
    'आपका browser हमारे न्यूनतम समर्थन ({min}) से नीचे है। आप कोशिश कर सकते हैं, पर अगर कैमरा काम न करे तो कृपया update करें या दूसरा फ़ोन इस्तेमाल करें।',

  // US-03482 P1-3: लोकेशन गेट — फ़ोन से लोकेशन मिलने तक कोई शॉट शुरू नहीं होता।
  'sv.geo.gate.title': 'आगे बढ़ने के लिए लोकेशन चालू करें',
  'sv.geo.gate.why': 'हर फ़ोटो और वीडियो में दर्ज होता है कि वह कहाँ लिया गया, ताकि {tenantName} परिसर की पुष्टि कर सके।',
  'sv.geo.gate.waiting': 'आपकी लोकेशन ली जा रही है…',
  'sv.geo.gate.off.lead': 'हमें आपकी लोकेशन नहीं मिल रही। हो सकता है आपके फ़ोन की लोकेशन बंद हो।',
  'sv.geo.gate.denied.lead': 'इस पेज के लिए लोकेशन ब्लॉक है। आगे बढ़ने के लिए इसकी अनुमति दें।',
  'sv.geo.gate.off.android.1': 'Quick settings (क्विक सेटिंग्स) खोलने के लिए स्क्रीन के ऊपर से नीचे की ओर स्वाइप करें।',
  'sv.geo.gate.off.android.2': 'Location (लोकेशन) चालू करने के लिए उस पर टैप करें।',
  'sv.geo.gate.off.android.3': 'यहाँ वापस आएँ। हम अपने-आप फिर से जाँचेंगे, या फिर से कोशिश करें पर टैप करें।',
  'sv.geo.gate.off.ios.1': 'अपने फ़ोन की Settings (सेटिंग्स) खोलें।',
  'sv.geo.gate.off.ios.2': 'Privacy & Security (गोपनीयता और सुरक्षा) में जाएँ, फिर Location Services (लोकेशन सेवाएँ) चालू करें।',
  'sv.geo.gate.off.ios.3': 'यहाँ वापस आएँ। हम अपने-आप फिर से जाँचेंगे, या फिर से कोशिश करें पर टैप करें।',
  'sv.geo.gate.off.other.1': 'अपने डिवाइस की Settings (सेटिंग्स) में Location (लोकेशन / GPS) चालू करें।',
  'sv.geo.gate.off.other.2': 'पक्का करें कि इस ब्राउज़र को आपकी लोकेशन इस्तेमाल करने की अनुमति है।',
  'sv.geo.gate.off.other.3': 'यहाँ वापस आएँ। हम अपने-आप फिर से जाँचेंगे, या फिर से कोशिश करें पर टैप करें।',
  'sv.geo.gate.off.android.app':
    'Location पहले से चालू है? Settings (सेटिंग्स) खोलें, फिर Apps (ऐप्स), फिर अपना ब्राउज़र (जैसे Chrome), फिर Permissions (अनुमतियाँ), फिर Location (लोकेशन), और Allow (अनुमति दें) चुनें।',
  'sv.geo.gate.off.ios.app': 'उसी सूची में {app} पर टैप करें और While Using the App (ऐप इस्तेमाल करते समय) चुनें।',
  'sv.geo.gate.retry': 'फिर से कोशिश करें',
  'sv.geo.gate.ios.lead': 'इस पेज के लिए लोकेशन की अनुमति नहीं दी गई।',
  'sv.geo.gate.ios.1': 'यह पेज फिर से लोड करने के लिए नीचे Reload (फिर से लोड करें) पर टैप करें।',
  'sv.geo.gate.ios.2': 'जब फ़ोन आपकी लोकेशन इस्तेमाल करने के लिए पूछे, तो Allow (अनुमति दें) पर टैप करें।',
  'sv.geo.gate.ios.3': 'नहीं पूछा गया? Settings (सेटिंग्स) खोलें, फिर Privacy & Security (गोपनीयता और सुरक्षा), फिर Location Services (लोकेशन सेवाएँ) — इसे चालू रखें, {app} के लिए भी।',
  'sv.geo.gate.reload': 'फिर से लोड करें',
  'sv.geo.gate.refining': 'ज़्यादा सटीक लोकेशन ली जा रही है — अभी लगभग ±{meters} मी…',
  'sv.geo.gate.checking': 'फिर से जाँच रहे हैं…',
  'sv.geo.gate.timeout.lead': 'हम अभी भी आपकी लोकेशन ढूँढ रहे हैं। खिड़की के पास जाएँ या बाहर निकलें — हम कोशिश करते रहेंगे।',
  'sv.geo.gate.dismissed.lead': 'आपके ब्राउज़र ने लोकेशन की अनुमति माँगी थी, पर वह नहीं दी गई। फिर से कोशिश करें पर टैप करें, फिर ब्राउज़र के पूछने पर Allow (अनुमति दें) पर टैप करें।',
  'sv.geo.gate.asking.lead':
    'आपका ब्राउज़र आपकी लोकेशन इस्तेमाल करने की अनुमति माँग रहा है। पूछे जाने पर Allow (अनुमति दें) पर टैप करें — या दोबारा पूछे जाने के लिए फिर से कोशिश करें पर टैप करें।',
  'sv.geo.gate.webview.lead':
    'इस app का अंदरूनी browser आपकी लोकेशन नहीं भेज सकता और कैमरा भी इस्तेमाल नहीं कर सकता। लिंक कॉपी करके Chrome या Safari में खोलें।',

  'sv.review.title': 'जमा करने के लिए तैयार',
  'sv.review.sub':
    'नीचे दी गई हर चीज़ इसी फ़ोन पर सहेजी गई है। जमा करने से पहले अपलोड पीछे-पीछे पूरा हो जाता है।',
  'sv.review.waiting': 'अपलोड पूरा होने का इंतज़ार है…',
  'sv.review.missing.title': 'कुछ आवश्यक शॉट नहीं हैं',
  'sv.review.missing.body': 'आपका जमा स्वीकार करने से पहले सर्वर को ये चाहिए:',
  'sv.review.goTo': 'अभी रिकॉर्ड करें',
  'sv.review.submitFail':
    'हम अभी जमा नहीं कर सके। आपकी रिकॉर्डिंग इसी फ़ोन पर सुरक्षित हैं — फिर कोशिश करें।',
  // DEC-097 T5 — कैप्चर की स्थिति, कोई नतीजा नहीं (इस समय किसी ने कुछ भी नहीं जाँचा है)।
  'sv.review.item.done': 'भेज दिया',
  'sv.review.item.pending': 'भेजा जा रहा है',
  'sv.review.item.refused': 'दोबारा लें',
  'sv.review.item.skipped': 'छोड़ा गया',

  'sv.done.title': 'जमा हो गया',
  'sv.done.sub': 'धन्यवाद। आपकी रिकॉर्डिंग मिल गई हैं और एक व्यक्ति उनकी समीक्षा करेगा।',
  'sv.done.reference': 'संदर्भ',
  'sv.done.receipt': 'रसीद',
  'sv.done.captured': 'आपने ये चरण पूरे किए',
  'sv.done.next': 'आगे क्या होगा',
  'sv.done.next.body':
    '{tenantName} का एक समीक्षक रिकॉर्डिंग जाँचता है। नतीजा वही तय करते हैं — कोई स्वचालित सिस्टम नहीं। अगर और कुछ चाहिए होगा, तो आपको नया लिंक मिलेगा।',
  'sv.done.concern.title': 'चिंता दर्ज करें',
  'sv.done.concern.body':
    'अगर इस अनुरोध के बारे में कुछ भी परेशान करने वाला लगा हो, तो {tenantName} से उस माध्यम से संपर्क करें जिस पर आप पहले से भरोसा करते हैं, और ऊपर दिया संदर्भ बताएँ।',
  'sv.done.limits.title': 'यह रिकॉर्ड क्या दिखाता है',
  'sv.done.limits.chain':
    'हर रिकॉर्डिंग को भेजते समय आपके फ़ोन पर hash chain से सील किया गया, जो समय, लोकेशन और इस अनुरोध से बँधी है। इससे पता चलता है कि सर्वर को ठीक वही मिला जो इस फ़ोन ने भेजा, उसी क्रम में।',
  'sv.done.limits.signed': 'रिकॉर्डिंग पर इसी डिवाइस की एक key से हस्ताक्षर भी किए गए।',
  'sv.done.limits.unsigned': 'यह डिवाइस रिकॉर्डिंग पर हस्ताक्षर नहीं कर सका; समीक्षक यह देखते हैं।',
  'sv.done.limits.geo.missing': 'इस फ़ोन पर कोई लोकेशन दर्ज नहीं हुई; समीक्षक यह देखते हैं।',
  'sv.done.memory':
    'इस डिवाइस पर tab बंद करने से अपलोड नहीं बचते। जब तक हर शॉट "भेज दिया" न दिखाए, इसे खुला रखें।',
  'sv.done.close': 'आप यह पेज बंद कर सकते हैं।',

  // ── live leg (SV-5-2) ─────────────────────────────────────────────────────
  'sv.live.card.title': 'लाइव सत्यापन में शामिल हों',
  'sv.live.card.body':
    'एक अधिकारी ने रिकॉर्ड की जाने वाली कॉल पर आपका परिसर देखने को कहा है। जब आप परिसर पर हों और तैयार हों, तब जुड़ें।',
  'sv.live.card.action': 'अभी जुड़ें',
  'sv.live.title': 'लाइव सत्यापन में शामिल हों',
  'sv.live.sub': '{tenantName} का एक अधिकारी रिकॉर्ड की जाने वाली कॉल पर आपको देखेगा और आपसे बात करेगा।',
  'sv.live.pill.recording': 'रिकॉर्डिंग जारी',
  'sv.live.waiting.officer': 'अधिकारी के जुड़ने की प्रतीक्षा है…',
  'sv.live.connecting': 'आपका कैमरा और माइक्रोफ़ोन जोड़ा जा रहा है…',
  'sv.live.waiting.body':
    'अभी किसी अधिकारी ने आपकी कॉल नहीं ली है। यह पेज हर कुछ सेकंड में जाँचता है और अधिकारी के आते ही आपको जोड़ देगा।',
  'sv.live.waiting.keepOpen': 'यह पेज खुला रखें। अधिकारी के जुड़ने तक कुछ भी रिकॉर्ड नहीं होता।',
  'sv.live.expect.title': 'क्या होगा',
  'sv.live.expect.officer': 'अधिकारी आपके कैमरे से आपको और आपके परिसर को देखेगा।',
  'sv.live.expect.prompts': 'वे आपसे परिसर के कुछ हिस्से दिखाने को कह सकते हैं। उनके अनुरोध इस स्क्रीन पर लिखित रूप में दिखेंगे।',
  'sv.live.expect.recorded': 'यह कॉल रिकॉर्ड होती है और {tenantName} इसे आपकी पिछली रिकॉर्डिंग के साथ रखता है।',
  'sv.live.action.leave': 'अभी के लिए छोड़ें',
  'sv.live.action.hangUp': 'कॉल छोड़ें',
  'sv.live.action.rejoin': 'कॉल में फिर जुड़ें',
  'sv.live.reconnecting': 'फिर से जोड़ा जा रहा है…',
  'sv.live.officerHere': 'अधिकारी कॉल पर हैं।',
  'sv.live.officerJoining': 'जुड़ गए। अधिकारी आ रहे हैं।',
  'sv.live.media.permission':
    'हम आपके कैमरे या माइक्रोफ़ोन तक नहीं पहुँच पा रहे। कृपया ब्राउज़र में अनुमति दें, फिर दोबारा जुड़ें।',
  'sv.live.media.other': 'आपका कैमरा और माइक्रोफ़ोन जोड़ने में दिक्कत आ रही है। कृपया थोड़ी देर में फिर कोशिश करें।',
  'sv.live.prompts.title': 'अधिकारी के अनुरोध',
  'sv.live.prompts.none': 'अभी कोई अनुरोध नहीं। अधिकारी आपका मार्गदर्शन करेंगे।',
  'sv.live.prompt.kind.PAN': 'कैमरा धीरे-धीरे घुमाएँ',
  'sv.live.prompt.kind.HOLD': 'कैमरा स्थिर रखें',
  'sv.live.prompt.kind.WALK': 'कैमरा चालू रखकर चलें',
  'sv.live.prompt.kind.READ': 'ज़ोर से पढ़ें',
  'sv.live.prompt.kind.OTHER': 'अधिकारी का अनुरोध',
  'sv.live.prompt.done': 'हो गया — मैंने यह दिखा दिया',
  'sv.live.prompt.history': 'पहले के अनुरोध',
  'sv.live.prompt.state.COMPLETED': 'हो गया',
  'sv.live.prompt.state.EXPIRED': 'समय पूरा हो गया',
  'sv.live.prompt.state.CANCELLED': 'अधिकारी ने वापस लिया',
  'sv.live.consent.lead': 'जुड़ने से पहले, अधिकारी की लाइव कॉल के लिए नीचे दी गई चीज़ों पर आपकी सहमति ज़रूरी है।',
  'sv.live.ended.title': 'अधिकारी ने सत्र समाप्त किया',
  'sv.live.ended.body':
    'धन्यवाद। कॉल रिकॉर्ड हो गई और {tenantName} के पास आपके अनुरोध में जोड़ दी गई। एक व्यक्ति इसकी समीक्षा करेगा।',
  'sv.live.left.title': 'आपने कॉल छोड़ दी',
  'sv.live.left.body': 'अधिकारी शायद अब भी प्रतीक्षा कर रहे हों। सत्र खुला रहने तक आप फिर जुड़ सकते हैं।',
  'sv.live.unavailable.title': 'जुड़ने के लिए कोई लाइव कॉल नहीं है',
  'sv.live.unavailable.body':
    'यह अनुरोध किसी लाइव कॉल की प्रतीक्षा में नहीं है। अगर आपसे जुड़ने को कहा गया था, तो लिंक भेजने वाली संस्था से संपर्क करें।',
  'sv.live.superseded.title': 'यह कॉल किसी दूसरे डिवाइस पर खोली गई',
  'sv.live.superseded.body': 'वहीं जारी रखें। यह पेज डिस्कनेक्ट कर दिया गया है।',
  'sv.live.unreachable.title': 'हम कॉल तक नहीं पहुँच सके',
  'sv.live.unreachable.body': 'अपना कनेक्शन जाँचें और फिर कोशिश करें।',

  // ── ROUND 2 (SPEC_CAPTURE_UX_R2, phases 1 + 2) — AWAITING NATIVE REVIEW (authored 2026-09-25 in the notices' आप-register;
  //    a human Hindi reviewer signs these off before cutover; the ledger tool has no review flag, so this comment is the mark) ──
  'sv.help.button': 'मदद',
  'sv.help.title': 'मदद',
  'sv.help.close': 'बंद करें',
  'sv.help.why.title': 'यह शॉट क्यों?',
  'sv.help.why.generic': 'इससे {tenantName} को यह पक्का करने में मदद मिलती है कि यह जगह असली है और इस्तेमाल में है।',
  'sv.help.why.EXT': 'इससे {tenantName} आपकी दुकान का नाम और बोर्ड अपने रिकॉर्ड से मिला पाता है।',
  'sv.help.why.ENTRANCE': 'इससे {tenantName} को अंदर जाने का रास्ता दिखता है — कि दरवाज़ा असली है और वहाँ पहुँचा जा सकता है।',
  'sv.help.why.INT': 'इससे {tenantName} को अंदर से काम की जगह दिखती है, एक बार चारों ओर।',
  'sv.help.why.DOOR_PLATE': 'इससे {tenantName} अपने रिकॉर्ड का यूनिट या दरवाज़े का नंबर पढ़ पाता है।',
  'sv.help.why.CERT_WALL': 'इससे {tenantName} को आपके लगाए हुए पंजीकरण दिखते हैं।',
  'sv.help.why.SIGNBOARD': 'इससे {tenantName} एक साफ़ फ़ोटो से व्यवसाय का नाम और GST नंबर ठीक से पढ़ पाता है।',
  'sv.help.why.ACTIVITY': 'इससे {tenantName} को दिखता है कि यहाँ रोज़ काम होता है।',
  'sv.help.why.INSTALL_POINT': 'इससे {tenantName} को दिखता है कि कनेक्शन कहाँ लगेगा।',
  'sv.help.why.HOME': 'इससे {tenantName} को आपका दिया हुआ घर का पता बाहर से दिखता है।',
  'sv.help.why.REP_SELFIE': 'इससे {tenantName} देख पाता है कि सत्यापन कौन कर रहा है।',
  'sv.help.why.ID_DOCUMENT': 'इससे {tenantName} आपका नाम आपके दस्तावेज़ से मिला पाता है।',
  'sv.help.why.CHALLENGE': 'इससे पता चलता है कि रिकॉर्डिंग अभी, इसी अनुरोध के लिए की गई है।',
  'sv.help.why.screen': 'इससे जाँचा जाता है कि {tenantName} के रिकॉर्ड की जगह असली है और इस्तेमाल में है।',
  'sv.help.good.title': 'अच्छा शॉट कैसा दिखता है',
  'sv.help.showMe': 'मुझे दिखाएँ',
  'sv.help.cap.generic': 'शॉट सेट करें',
  'sv.help.cant.title': 'अगर आप यह नहीं कर पा रहे',
  'sv.help.skip.title': 'यह शॉट छोड़ें',
  'sv.help.skip.why': 'कारण बताएँ (वैकल्पिक)',
  'sv.help.skip.reason.reach': 'मैं इस जगह तक नहीं पहुँच सकता/सकती',
  'sv.help.skip.reason.none': 'यह यहाँ है ही नहीं',
  'sv.help.skip.reason.unsafe': 'अभी यह सुरक्षित नहीं है',
  'sv.help.skip.reason.other': 'कुछ और',
  'sv.help.skip.confirm': 'यह छोड़ें',
  'sv.help.skip.note': 'वैकल्पिक शॉट छोड़ने से आपका अनुरोध कभी नहीं रुकता।',
  'sv.help.call.line': 'मदद चाहिए? हमसे कॉल करवाएँ।',
  'sv.help.call.button': 'कॉल के लिए कहें',
  'sv.help.call.reason': 'किस बात में मदद चाहिए? (वैकल्पिक)',
  'sv.help.call.sending': 'भेजा जा रहा है…',
  'sv.help.call.failed': 'यह भेजा नहीं जा सका। फिर कोशिश करें।',
  'sv.help.recovery.title': 'कैमरा या लोकेशन बंद है?',
  'sv.perm.platform.android': 'Android (Chrome) पर',
  'sv.perm.platform.ios': 'iPhone पर',
  'sv.perm.platform.other': 'आपके ब्राउज़र में',
  'sv.perm.ios.app.other': 'ब्राउज़र ऐप',
  'sv.perm.cam.title': 'कैमरा चालू करें',
  'sv.perm.cam.ios.1': 'अपने फ़ोन की Settings खोलें।',
  'sv.perm.cam.ios.2': '{app} में जाएँ, फिर Camera।',
  'sv.perm.cam.ios.3': "'Allow' चुनें, फिर यहाँ लौटकर नीचे का बटन दबाएँ।",
  'sv.perm.loc.title': 'लोकेशन चालू करें',
  'sv.perm.loc.android.1': 'पते के बगल में ताले के आइकन पर टैप करें।',
  'sv.perm.loc.android.2': 'Permissions पर टैप करें, फिर Location।',
  'sv.perm.loc.android.3': 'Allow चुनें, फिर यहाँ लौट आएँ।',
  'sv.perm.loc.ios.1': 'अपने फ़ोन की Settings खोलें।',
  'sv.perm.loc.ios.2': 'Privacy & Security में जाएँ, फिर Location Services, फिर {app}।',
  'sv.perm.loc.ios.3': "'While Using the App' चुनें, फिर यहाँ लौट आएँ।",
  'sv.perm.loc.other.1': 'इस पेज के लिए अपने ब्राउज़र की site settings खोलें।',
  'sv.perm.loc.other.2': 'अनुमतियों की सूची में Location ढूँढें।',
  'sv.perm.loc.other.3': 'उसे Allow पर सेट करें, फिर यहाँ लौट आएँ।',
  'sv.perm.rec.then': 'इस दौरान कुछ भी खोता नहीं है।',
  'sv.capture.location.howTo': 'कैसे अनुमति दें',
  'sv.queue.saved': 'इस फ़ोन पर सुरक्षित · भेजा जाएगा',
  'sv.queue.savedOffline': 'इस फ़ोन पर सुरक्षित · ऑनलाइन होते ही भेजा जाएगा',
  'sv.queue.keepOpen': 'भेजे जाने तक यह पेज खुला रखें',
  'sv.queue.onePiece': '1 हिस्सा अभी अपलोड हो रहा है',
  'sv.consent.companion': 'आप यह किसी परिवार के सदस्य की मदद से कर सकते हैं — बस उन्हें शॉट में न लें।',
  'sv.lang.title': 'अपनी भाषा चुनें',
  'sv.lang.sub': 'आप इसे कभी भी बदल सकते हैं।',
  'sv.lang.suggested': 'सुझाई गई',
  'sv.lang.lowData.title': 'कम डेटा इस्तेमाल करें',
  'sv.lang.lowData.sub': 'मैप और डेमो तभी लोड होंगे जब आप कहेंगे।',
  'sv.lang.lowData.auto': 'इस कनेक्शन पर चालू',
  'sv.hint.dark': 'थोड़ा अँधेरा है',
  'sv.hint.moving': 'स्थिर रखें',
  'sv.hint.closer': 'पास आएँ',
  'sv.hint.back': 'पीछे हटें',
  'sv.affirm.good': 'बढ़िया शॉट',
  'sv.capture.stamp.map.show': 'मैप दिखाएँ',
  'sv.review.skipReason': 'छोड़ा गया — {reason}',

  // ── राउंड 2, फ़ेज़ 3 — दिखाने वाले चित्र (§4) और आवाज़ वाली मदद (§3) ─────────────────────────────
  // AWAITING NATIVE REVIEW (see the REVIEW STATUS note at the top of this file): authored 2026-09-25 in the same
  // आप-form register as the residence notice v2; the human linguistic review is an external gate, undischarged.
  'sv.showme.STREET.cap': 'सड़क के उस पार खड़े हों',
  'sv.showme.SIDEWAYS.cap': 'फ़ोन को आड़ा घुमाएँ',
  'sv.showme.DOOR.cap': 'दरवाज़े तक चलें',
  'sv.showme.TURN.cap': 'धीरे-धीरे एक बार घूमें',
  'sv.showme.PLATE.cap': 'नाम-पट्टिका के पास जाएँ',
  'sv.showme.CERTIFICATES.cap': 'दीवार पर लगे प्रमाणपत्र दिखाएँ',
  'sv.showme.FACE.cap': 'सीधे कैमरे की ओर देखें',
  'sv.showme.ID_FLAT.cap': 'ID को सपाट और अच्छी रोशनी में पकड़ें',
  'sv.showme.title': 'यह शॉट कैसे लें',
  'sv.showme.gotIt': 'ठीक है',
  'sv.showme.close': 'बंद करें',
  'sv.showme.next': 'आगे',
  'sv.showme.again': 'फिर दिखाएँ',
  'sv.audio.replay': 'निर्देश फिर सुनाएँ।',
  'sv.audio.mute': 'आवाज़ बंद करें',
  'sv.audio.unmute': 'आवाज़ चालू करें',
  'sv.audio.unavailable': '{language} में आवाज़ वाली मदद अभी उपलब्ध नहीं है — नीचे दिए चरण पढ़ें।',
  'sv.audio.listenNotice': 'यह सूचना सुनें',
  'sv.audio.listenNotice.aid':
    'आप यह सूचना सुन सकते हैं या पढ़ सकते हैं। दोनों हालत में, तैयार होने पर आपको ‘{action}’ पर टैप करना होगा।',
  'sv.audio.stop': 'रोकें',
};
